/** @jsx jsx */
import { jsx } from '@emotion/react';
import { useState } from 'react';
import { StaticQuery, graphql } from 'gatsby';
import styled from '@emotion/styled';
import config from '../../../config';
import Logo from './logo';
import { HeaderNavigation } from './HeaderNavigation';
import { Menu, Facebook, GitHub, Linkedin, Slack, Twitter } from 'react-feather';
import { globalHistory } from '@reach/router';
import { onSm, onMd, onLg, onXlg, onXxlg } from '../../styles/responsive';
import { IconButton } from '../Buttons/IconButton';
import { SocialButton } from '../Buttons/SocialButton';
import logoImg from '../../../static/assets/favicon.png';
import { SidebarNavigation } from '../Sidebar/Sidebar';
import { colors, headerHeight, shadows } from '../../theme';
import { decreaseIntensivity } from '../../utils/colors';
import AlgoliaSearch from '../Search/algolia/AlgoliaSearch';

const SocialButtons = ({ showOnSm }: { showOnSm: boolean }) => {
  return (
    <div
      css={{
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        margin: '0px !important',
        [onSm]: {
          display: showOnSm ? 'flex' : 'none',
          justifyContent: 'space-evenly',
          padding: '10px 17px'
        }
      }}
    >
      <SocialButton link={config.social.slack} icon={Slack} />
      <SocialButton link={config.social.github} icon={GitHub} />
      <SocialButton link={config.social.facebook} icon={Facebook} />
      <SocialButton link={config.social.linkedin} icon={Linkedin} />
      <SocialButton link={config.social.twitter} icon={Twitter} />
    </div>
  );
};

export const Header = ({ location }) => (
  <StaticQuery
    query={graphql`
      query headerTitleQuery {
        site {
          siteMetadata {
            headerTitle
            logo {
              link
              image
            }
            headerLinks {
              link
              text
              external
            }
          }
        }
      }
    `}
    render={(data) => {
      const {
        site: {
          siteMetadata: { headerTitle, logo, headerLinks }
        }
      } = data;
      const logoLink = logo.link !== '' ? logo.link : '/';
      const [menuOpen, setMenuOpen] = useState(false);
      const toggleMenuOpen = () => setMenuOpen(!menuOpen);
      globalHistory.listen(() => {
        setMenuOpen(false);
      });
      const mobileDividerStyle = {
        border: '1px solid #303030',
        marginLeft: '26px',
        marginRight: '26px'
      };
      return (
        <div
          css={{
            backgroundColor: colors.darkerBackground,
            borderRadius: 0,
            border: 0,
            display: 'flex',
            alignItems: 'center',
            boxShadow: shadows.boxShadow,
            zIndex: 1,
            [onMd]: {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0
            },
            [onSm]: {
              flexWrap: 'wrap'
            }
          }}
        >
          <div
            css={{
              height: headerHeight,
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              [onXlg]: {
                marginLeft: '125px',
                marginRight: '125px'
              },
              [onXxlg]: {
                marginLeft: '273px',
                marginRight: '305px'
              }
            }}
          >
            <Logo link={logoLink} img={logoImg} title={headerTitle} />
            <div
              css={{
                transition: 'top 0.5s, bottom 0.5s',
                flex: '1 1 auto',
                padding: '10px 0',
                display: 'none',
                [onLg]: {
                  display: 'flex',
                  marginRight: '30px'
                },
                [onMd]: {
                  display: 'flex',
                  marginRight: '5px'
                }
              }}
            >
              <HeaderNavigation links={headerLinks} />
            </div>
            <div
              css={{
                display: 'flex',
                flex: 'auto',
                justifyContent: 'flex-end',
                alignItems: 'center',
                minWidth: 'auto',
                marginRight: '25px',
                a: {
                  margin: '0 6px'
                },
                '> div': {
                  margin: '0 6px'
                }
              }}
            >
              <AlgoliaSearch />
              <SocialButtons showOnSm={false} />
              <IconButton
                css={{
                  display: 'none',
                  [onSm]: {
                    display: 'flex'
                  }
                }}
                title="Open menu"
                background={colors.background}
                hoverStroke={colors.primary}
                fill="transparent"
                stroke={menuOpen ? colors.primary : decreaseIntensivity(colors.background, 0.4)}
                icon={Menu}
                onClick={toggleMenuOpen}
              />
            </div>
          </div>
          <div
            css={{
              display: 'none',
              [onSm]: {
                display: menuOpen ? 'flex !important' : 'none !important'
              },
              flexBasis: '100%',
              flexDirection: 'column'
            }}
          >
            <hr css={mobileDividerStyle} />
            <AlgoliaSearch showOnSm rootCss={{ padding: '2px 24px' }} />
            <hr css={mobileDividerStyle} />
            <HeaderNavigation links={headerLinks} />
            <hr css={mobileDividerStyle} />
            <SocialButtons showOnSm />
            <hr css={mobileDividerStyle} />
            <SidebarNavigation showOnSm location={location} />
          </div>
        </div>
      );
    }}
  />
);
